import { memo, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { FormattedDate, FormattedMessage } from "react-intl";
import AmountContainer from "app/pages/.shared/AmountContainer";
import { computeActivityTotal } from "app/utils/utils";
import "./QuotationActivityDate.scss";
import { sendTagOnActivityDateChange } from "app/utils/analytics";
import AdvancedSelectFormik from "app/pages/.shared/form/AdvancedSelectFormik/AdvancedSelectFormik";
import { useFormikContext } from "formik";

const QuotationActivityDate = ({
	availableDates,
	onChange,
	activityCode,
	guestsPerDay,
	hasIncludedGuest,
	index,
}) => {
	const { values, errors } = useFormikContext();
	const currentActivity = values?.activities?.find(act => act.code === activityCode);

	const dates = availableDates.map(availableDate => {
		const numberOfParticipants = currentActivity?.guests;

		const guests = guestsPerDay[availableDate];

		const pricingPerParticipant = (guests || []).reduce((acc, { code, pricePerQuantity }) => {
			acc[code] = Object.fromEntries(
				Object.entries(pricePerQuantity).map(([quantity, { price = 0, discount = 0 }]) => [
					quantity,
					{ price, discount },
				])
			);
			return acc;
		}, {});

		const total = computeActivityTotal(pricingPerParticipant, numberOfParticipants);

		return {
			code: availableDate,
			label: (
				<>
					<FormattedDate
						timeZone="UTC"
						month="short"
						day="numeric"
						weekday="short"
						value={availableDate}
					/>
					<AmountContainer amount={total} />
				</>
			),
		};
	});

	const activityDate = currentActivity?.date;
	const hasMultipleDates = dates.length > 1;
	const activityLabelClassName = classNames("quotation-activity-date", {
		"quotation-activity-date--single-date": !hasMultipleDates,
	});

	const selectClassName = classNames(
		{ "activity-date--error": errors?.duplicateDates?.includes(activityDate) },
		"activity-date"
	);

	useEffect(() => {
		onChange(activityDate);
	}, [activityDate]);

	const handleDateChange = useCallback(({ code }) => {
		sendTagOnActivityDateChange();
		onChange(code);
	}, []);

	return (
		<div className={activityLabelClassName}>
			<div className="quotation-activity-date__label">
				{!hasIncludedGuest && (
					<>
						<FormattedMessage
							id={
								hasMultipleDates
									? "quotation.activity.date.label"
									: "quotation.activity.single.date.label"
							}
						/>
						{hasMultipleDates && (
							<div className="quotation-activity-date__label-maxday">
								<FormattedMessage id="quotation.activity.date.label.max" />
							</div>
						)}
					</>
				)}
			</div>

			{hasMultipleDates ? (
				<div className="quotation-activity-date__select">
					<AdvancedSelectFormik
						id={`activities[${index}].date`}
						name={`activities[${index}].date`}
						value={{
							code: activityDate,
							label: (
								<FormattedDate
									timeZone="UTC"
									month="short"
									day="numeric"
									weekday="short"
									value={activityDate}
								/>
							),
						}}
						classNamePrefix={selectClassName}
						isSearchable={false}
						openMenuOnFocus
						getOptionLabel={({ label }) => label}
						getOptionValue={({ code }) => code}
						blurInputOnSelect={true}
						options={dates}
						onChange={handleDateChange}
						menuPortalMargin={-20}
					/>
				</div>
			) : (
				<div className="quotation-activity-date__date">
					<FormattedDate
						timeZone="UTC"
						month="short"
						day="numeric"
						weekday="short"
						value={activityDate}
					/>
				</div>
			)}
		</div>
	);
};

QuotationActivityDate.propTypes = {
	guestsPerDay: PropTypes.object,
	availableDates: PropTypes.array,
	activityCode: PropTypes.string,
	onChange: PropTypes.func,
	hasIncludedGuest: PropTypes.bool,
	index: PropTypes.number,
};

QuotationActivityDate.defaultProps = {
	guestsPerDay: {},
	availableDates: [],
	onChange: () => {},
};

export default memo(QuotationActivityDate);
